/**
 * フォーム設定タブ (2026-07-28 新設)。
 *
 * ビルダーに散らばっていた「送信後の挙動 / 受付条件」系の設定をここへ集約する。
 * ビルダーは “何を訊くか”、設定タブは “受け取った後どうするか” に責務を分ける。
 *
 * 旧 UI からの改善点:
 *   - 長い説明文を常時展開せず SettingDetails に畳んだ（一覧性）
 *   - 「Deal Stage ヒント」等の内部用語を業務語に置き換え（理解性）
 *   - 条件を満たさない設定を非表示にせず、理由付きで淡色表示（発見性）
 *   - セクション見出しに現在値サマリを出し、開かなくても状態が分かる（視認性）
 */
import {
  categoryFromDealStageHint,
  type ContactOverwritePolicy,
  type DealStageHint,
} from '@goenhub/shared';
import { Timestamp } from 'firebase/firestore';
import { Briefcase, CalendarClock, Eye, Save, UserCog } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { FormPageTabs } from './FormPageTabs';
import {
  SettingDetails,
  SettingRow,
  SettingToggle,
  SettingsSection,
} from './settings/SettingsPrimitives';
import { useFormEditor } from './useFormEditor';

import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useDepartments } from '@/features/permissions';
import { useDocuments } from '@/hooks/useDocuments';
import { useProductCategories, useUpsertProductCategory } from '@/hooks/useProductCategories';
import { toDatetimeLocalValue } from '@/lib/firestoreTime';

/** datetime-local の文字列 → Timestamp（空文字は null）。 */
function localToTimestamp(value: string): Timestamp | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return null;
  return Timestamp.fromMillis(ms);
}

/** 商談の種類（旧「Deal Stage ヒント」）の業務語ラベル。 */
const DEAL_STAGE_LABELS: Array<{ value: string; label: string }> = [
  { value: '__none__', label: '指定しない' },
  { value: 'document_requested', label: '資料請求' },
  { value: 'briefing', label: '個別説明の申込' },
  { value: 'quote', label: '見積依頼' },
];

export function FormSettingsPage() {
  const { id } = useParams<{ id: string }>();
  const { form, isLoading, dirty, saving, update, save } = useFormEditor(id);
  const { data: productCategories } = useProductCategories();
  const { data: departments } = useDepartments();
  const { data: allDocuments } = useDocuments();
  const upsertCategory = useUpsertProductCategory();

  const [newCategoryMode, setNewCategoryMode] = useState(false);
  const [newCategoryDraft, setNewCategoryDraft] = useState('');
  // 受付上限の入力中テキスト（空欄→無制限の確定は blur まで遅延させる）
  const [capacityText, setCapacityText] = useState('');

  useEffect(() => {
    setCapacityText(typeof form?.capacity === 'number' ? String(form.capacity) : '');
  }, [form?.capacity]);

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  const isSales = form.tag === 'sales';
  // 顧客情報確認・更新フォーム (2026-08-24): sales タブに属するが Deal を作らない種別。
  // 「商談の種類」を触らせない (誤って Deal 作成フォームへ変質するのを防ぐ)。
  const isCustomerInfo = isSales && form.category === 'customer_info';
  const hasBriefingPicker = (form.elements ?? []).some(
    (el) => el.kind === 'field' && el.fieldType === 'briefing_schedule_picker',
  );
  const capacityOn = typeof form.capacity === 'number';
  const publishedDocuments = (allDocuments ?? []).filter((d) => d.status === 'published');

  const handleAddCategory = async () => {
    const name = newCategoryDraft.trim();
    if (!name) return;
    const dup = (productCategories ?? []).find(
      (c) => c.name.trim().toLowerCase() === name.toLowerCase(),
    );
    if (dup) {
      update({ primaryCategoryId: dup.id, primaryCategory: dup.name });
      setNewCategoryMode(false);
      setNewCategoryDraft('');
      toast.success(`既存カテゴリー「${dup.name}」を選択しました`);
      return;
    }
    try {
      const r = await upsertCategory.mutateAsync({ name });
      update({ primaryCategoryId: r.id, primaryCategory: name });
      setNewCategoryMode(false);
      setNewCategoryDraft('');
      toast.success(`カテゴリー「${name}」を追加しました`);
    } catch (err) {
      toast.error(`追加失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const salesDisabledReason = isSales
    ? null
    : 'このフォームは営業フォームではないため設定できません。営業フローに繋ぐには、フォーム一覧から営業フォームとして作成してください。';

  return (
    <Screen
      title={`フォーム設定 — ${form.name}`}
      description="受付の条件と、送信後に何が起きるかをまとめて設定します"
      backTo="/campaigns?hub=forms"
      actions={
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => void save()}
          disabled={!dirty || saving}
        >
          {saving ? <Spinner className="size-3 mr-1" /> : <Save className="size-3.5 mr-1" />}
          {dirty ? '保存' : '保存済'}
        </Button>
      }
    >
      {id && (
        <FormPageTabs
          formId={id}
          submissionCount={form.submissionCount ?? 0}
          active="settings"
          dirty={dirty}
        />
      )}

      <div className="max-w-3xl space-y-4 pb-10">
        {/* ── 受付 ───────────────────────────── */}
        <SettingsSection
          icon={CalendarClock}
          title="受付"
          description="いつ・何件まで受け付けるか。公開ページの表示が自動で切り替わります。"
          summary={
            <span>
              {capacityOn ? (
                <>
                  上限 {form.capacity} 件
                  <span className="text-[var(--text-muted)]">
                    {' '}
                    / 現在 {form.submissionCount ?? 0} 件
                  </span>
                </>
              ) : (
                <span className="text-[var(--text-muted)]">件数は無制限</span>
              )}
            </span>
          }
        >
          <SettingRow
            title="公開期間"
            hint="期間外は「準備中」または「受付終了」表示になります。終了時刻を過ぎたフォームは 5 分以内に自動で受付終了になります。"
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">公開開始</Label>
                <Input
                  type="datetime-local"
                  value={toDatetimeLocalValue(form.publicStartAt)}
                  onChange={(e) => update({ publicStartAt: localToTimestamp(e.target.value) })}
                />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <Label className="text-xs !mb-0">公開終了</Label>
                  <label className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)] cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!form.publicEndAt}
                      onChange={(e) => {
                        if (e.target.checked) {
                          update({ publicEndAt: null });
                        } else {
                          const fallback = new Date();
                          fallback.setMonth(fallback.getMonth() + 1);
                          update({
                            publicEndAt: Timestamp.fromMillis(fallback.getTime()),
                          });
                        }
                      }}
                      className="size-3 accent-[var(--accent)]"
                    />
                    期限なし
                  </label>
                </div>
                <Input
                  type="datetime-local"
                  value={toDatetimeLocalValue(form.publicEndAt)}
                  disabled={!form.publicEndAt}
                  onChange={(e) => update({ publicEndAt: localToTimestamp(e.target.value) })}
                  className={!form.publicEndAt ? 'opacity-50' : undefined}
                />
              </div>
            </div>
          </SettingRow>

          <SettingRow
            title="受付上限（定員）"
            hint="上限に達すると、公開ページが自動で「定員に達しました」表示に切り替わります。"
          >
            <label className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)] cursor-pointer select-none mb-2">
              <input
                type="checkbox"
                checked={!capacityOn}
                onChange={(e) => update({ capacity: e.target.checked ? null : 10 })}
                className="size-3 accent-[var(--accent)]"
              />
              上限を設けない（無制限）
            </label>
            {capacityOn && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">受付上限</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={capacityText}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCapacityText(v);
                      // 入力途中の全消しで無制限化しないよう、空欄は blur まで保留
                      if (v !== '') {
                        update({
                          capacity: Math.max(0, Number.parseInt(v, 10) || 0),
                        });
                      }
                    }}
                    onBlur={() => {
                      if (capacityText === '') update({ capacity: null });
                    }}
                    placeholder="例: 10"
                  />
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">
                    現在 {form.submissionCount ?? 0} / {form.capacity} 件
                    {form.submissionCount !== undefined &&
                      form.capacity !== null &&
                      form.capacity !== undefined &&
                      form.submissionCount >= form.capacity && (
                        <span className="text-[var(--accent-red)] ml-1">(定員到達)</span>
                      )}
                    {form.capacity === 0 && (
                      <span className="text-[var(--accent-amber)] ml-1">(0 = 受付を即停止)</span>
                    )}
                  </p>
                </div>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">満員時メッセージ</Label>
                    <Input
                      type="text"
                      value={form.capacityFullMessage ?? ''}
                      onChange={(e) => update({ capacityFullMessage: e.target.value })}
                      placeholder="定員に達しました"
                    />
                  </div>
                  <label className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)] cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!form.showRemainingCount}
                      onChange={(e) => update({ showRemainingCount: e.target.checked })}
                      className="size-3 accent-[var(--accent)]"
                    />
                    公開ページに残枠数を表示
                  </label>
                </div>
              </div>
            )}
          </SettingRow>
        </SettingsSection>

        {/* ── 顧客データ ───────────────────────── */}
        <SettingsSection
          icon={UserCog}
          title="顧客データへの反映"
          description="送信内容を顧客台帳（オーディエンス）へどう書き込むかを決めます。"
          summary={
            <Badge variant={form.contactOverwritePolicy === 'overwrite' ? 'draft' : 'default'}>
              {form.contactOverwritePolicy === 'overwrite' ? '上書きする' : '空欄のみ補完'}
            </Badge>
          }
        >
          <SettingRow
            title="1 つしか値を持てない項目の扱い"
            hint="氏名 / 電話番号 / 会社名 / 英文住所が対象です。"
            control={
              <Select
                value={form.contactOverwritePolicy ?? 'fill'}
                onValueChange={(v) =>
                  update({
                    contactOverwritePolicy: v as ContactOverwritePolicy,
                  })
                }
              >
                <SelectTrigger className="h-8 text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fill">空欄のみ補完する</SelectItem>
                  <SelectItem value="overwrite">最新の回答で上書きする</SelectItem>
                </SelectContent>
              </Select>
            }
          >
            <SettingDetails>
              <p>
                {form.contactOverwritePolicy === 'overwrite'
                  ? '登録済みの値があっても送信された値で置き換えます。住所変更・英文住所の申告フォームなど「最新の申告を正としたい」場合に選びます。'
                  : '登録済みの値は保護され、空欄の項目だけが埋まります。アンケートや一般の問い合わせフォームはこちらのままにしてください。'}
              </p>
              <p>
                <strong>メールアドレス / 請求先住所 / 納品先住所はこの設定の対象外</strong>
                です。同じ内容なら更新、違う内容なら追加したうえで「既定」に切り替わります（以前の値は削除されず残ります）。
              </p>
              <p>
                氏名の一致だけで顧客が特定された送信では、同姓同名の別人による書き換えを防ぐため上書きは行われません。更新内容は顧客詳細の「変更履歴」に記録されます。
              </p>
            </SettingDetails>
          </SettingRow>
        </SettingsSection>

        {/* ── 営業連携 ─────────────────────────── */}
        <SettingsSection
          icon={Briefcase}
          title="営業連携"
          description="送信時に商談（Deal）を自動作成し、商談ボードに載せます。"
          summary={
            isCustomerInfo ? (
              <Badge variant="review">顧客情報確認・更新</Badge>
            ) : isSales ? (
              <Badge variant="accent">sales</Badge>
            ) : (
              <span className="text-[var(--text-muted)]">対象外</span>
            )
          }
        >
          {isCustomerInfo && (
            <p className="text-xs text-[var(--text-secondary)] rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2">
              このフォームは「顧客情報確認・更新」用です。送信されても
              <strong>商談（Deal）は作成・紐付けされません</strong>
              （住所確認は商談ごとではなく顧客につき一度で良い情報のため）。回答内容は顧客データへ自動反映されます。
            </p>
          )}
          <SettingRow
            title="商談の種類"
            hint="作成される商談の初期ステージになります。"
            disabledReason={
              salesDisabledReason ??
              (isCustomerInfo
                ? 'このフォームは「顧客情報確認・更新」用のため商談は作成されません。商談を作るフォームが必要な場合は、フォーム一覧から別種別で作成してください。'
                : null)
            }
            control={
              <Select
                value={form.dealStageHint ?? '__none__'}
                onValueChange={(v) => {
                  const hint = v === '__none__' ? null : (v as DealStageHint);
                  update({
                    dealStageHint: hint,
                    category: categoryFromDealStageHint(hint),
                  });
                }}
              >
                <SelectTrigger className="h-8 text-[12px]">
                  <SelectValue placeholder="指定しない" />
                </SelectTrigger>
                <SelectContent>
                  {DEAL_STAGE_LABELS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />

          <SettingRow
            title="主軸カテゴリ"
            hint="商談を既存の案件に紐付けるときの手がかりになります。空欄でも構いません。"
            disabledReason={salesDisabledReason}
          >
            {!newCategoryMode ? (
              <select
                value={form.primaryCategoryId ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__new__') {
                    setNewCategoryMode(true);
                    setNewCategoryDraft('');
                    return;
                  }
                  if (!v) {
                    update({ primaryCategoryId: null, primaryCategory: null });
                    return;
                  }
                  const c = (productCategories ?? []).find((x) => x.id === v);
                  update({
                    primaryCategoryId: v,
                    primaryCategory: c?.name ?? null,
                  });
                }}
                className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
              >
                <option value="">
                  未指定
                  {form.primaryCategory && !form.primaryCategoryId
                    ? ` (旧データ: ${form.primaryCategory})`
                    : ''}
                </option>
                {(productCategories ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
                <option value="__new__">＋ 新規カテゴリーを追加…</option>
              </select>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  value={newCategoryDraft}
                  onChange={(e) => setNewCategoryDraft(e.target.value)}
                  placeholder="例: プロ機器 / コンシューマ機器"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAddCategory();
                    } else if (e.key === 'Escape') {
                      setNewCategoryMode(false);
                      setNewCategoryDraft('');
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={() => void handleAddCategory()}
                  disabled={!newCategoryDraft.trim() || upsertCategory.isPending}
                >
                  {upsertCategory.isPending ? <Spinner className="size-3" /> : '追加'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setNewCategoryMode(false);
                    setNewCategoryDraft('');
                  }}
                >
                  キャンセル
                </Button>
              </div>
            )}
          </SettingRow>

          <SettingRow
            title="自動返信で送付する資料"
            hint="送信直後に、選んだ資料の閲覧 URL を記載した返信メールが自動送信されます。"
            disabledReason={
              salesDisabledReason ??
              (form.dealStageHint === 'document_requested'
                ? null
                : '「商談の種類」を “資料請求” にすると設定できます。')
            }
          >
            <select
              value={form.documentId ?? ''}
              onChange={(e) => update({ documentId: e.target.value || null })}
              className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
            >
              <option value="">(未選択 — 自動返信なし)</option>
              {publishedDocuments
                .filter((d) => (d.type ?? 'viewer') === 'viewer')
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    🔗 {d.title}
                  </option>
                ))}
              {publishedDocuments
                .filter((d) => d.type === 'pdf_download')
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    📄 {d.title} (PDF ダウンロード)
                  </option>
                ))}
            </select>
            {publishedDocuments.length === 0 ? (
              <p className="text-[10px] text-[var(--accent-amber)] mt-1">
                公開済みの資料がありません。先に「資料」メニューで作成・公開してください。
              </p>
            ) : (
              <p className="text-[10px] text-[var(--text-muted)] mt-1">
                🔗 公開リンク閲覧資料 / 📄 PDF ダウンロード資料
              </p>
            )}
            {form.documentId && (
              <p className="text-[10px] text-[var(--accent-green)] mt-1">
                ✓ 紐付け済み: 送信時に自動返信メールが送られます
              </p>
            )}
            <SettingDetails>
              <p>
                メールの文面は
                <span className="font-mono mx-0.5">
                  営業設定 &gt; 業務通知テンプレ &gt; 資料請求 自動返信
                </span>
                で編集できます。未選択のときは自動返信を送らず、商談の作成のみ行います。
              </p>
            </SettingDetails>
          </SettingRow>

          <SettingRow
            title="個別説明枠の表示対象"
            hint="紹介リンク（?sa= 付き）以外からの申込で、どの部署の担当者の枠を出すかを絞ります。何も選ばなければ全部署を表示します。"
            disabledReason={
              salesDisabledReason ??
              (hasBriefingPicker
                ? null
                : 'このフォームに「個別説明 候補日」の質問がないため設定できません。ビルダーで追加すると有効になります。')
            }
          >
            <div className="flex flex-wrap gap-3">
              {(departments ?? []).length === 0 && (
                <span className="text-[11px] italic text-[var(--text-muted)]">
                  部署マスタが空です
                </span>
              )}
              {(departments ?? []).map((d) => {
                const current = form.briefingDepartmentFilter ?? [];
                return (
                  <label
                    key={d.id}
                    className="flex items-center gap-1.5 text-xs cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      checked={current.includes(d.id)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? Array.from(new Set([...current, d.id]))
                          : current.filter((x) => x !== d.id);
                        update({
                          briefingDepartmentFilter: next.length > 0 ? next : null,
                        });
                      }}
                      className="size-3 accent-[var(--accent)]"
                    />
                    {d.name}
                  </label>
                );
              })}
            </div>
            <p className="text-[10px] text-[var(--accent)] mt-1.5">推奨: 「営業」のみチェック</p>
            <SettingDetails>
              <p>外部 SA（販売代理店）の枠は、紹介リンク以外からの申込では常に除外されます。</p>
            </SettingDetails>
          </SettingRow>

          <SettingToggle
            checked={!!form.isCompanyReferral}
            onChange={(v) => update({ isCompanyReferral: v })}
            label="弊社紹介として扱う"
            hint="ON にすると、このフォーム経由の商談は「弊社紹介」となり、SA の報酬はベース報酬率で計算されます。"
            disabledReason={salesDisabledReason}
          >
            <SettingDetails>
              <p>
                OFF のフォーム経由（= 開拓顧客）は、「報酬設定 &gt; SA タブ &gt;
                開拓顧客加算率」が上乗せされます (SA の個別設定に加算率があればそちら)。
              </p>
              <p>紹介者・社員の報酬には影響しません。個々の商談画面から上書きも可能です。</p>
            </SettingDetails>
          </SettingToggle>
        </SettingsSection>

        {/* ── 表示範囲 ─────────────────────────── */}
        <SettingsSection
          icon={Eye}
          title="表示範囲"
          description="社内以外にこのフォームを見せるかどうか。"
          summary={
            <Badge variant={form.visibleToSa !== false ? 'approved' : 'default'}>
              {form.visibleToSa !== false ? 'SA に表示' : 'SA に非表示'}
            </Badge>
          }
        >
          <SettingToggle
            checked={form.visibleToSa !== false}
            onChange={(v) => update({ visibleToSa: v })}
            label="SA ポータルに表示する"
            hint="外部 SA（販売代理店）のフォーム画面に、このフォームを載せます。"
          >
            <SettingDetails>
              <p>
                OFF にすると <span className="font-mono">/sa/forms</span> から非表示になり、SA
                に必要なフォームだけを見せられます。営業フォーム・マーケティングフォームのどちらにも適用されます。
              </p>
            </SettingDetails>
          </SettingToggle>
        </SettingsSection>
      </div>
    </Screen>
  );
}
